import React, { FC, ReactNode, useCallback, useMemo } from 'react';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import { forms } from '@factorin/container.macro';
import {
    Select as BaseSelect,
    SelectProps as BaseSelectProps,
    Input,
    inputBaseClasses,
    inputClasses,
} from '@mui/material';
import { FieldRenderProps } from 'react-final-form';

import { boxShadows } from 'Modules/ui3';

import { Typography } from '../../../ui3/components/Typography';
import { ArrowDown24 } from '../../../ui3/icons';


export interface Option {
    value: string | number;
    title: string | ReactNode;
}

type SelectedValue = Option['value'] | Option['value'][];

type SelectFieldProps = BaseSelectProps & FieldRenderProps<SelectedValue> & {
    options: Option[];
}

type SelectedValueProps = Pick<SelectFieldProps, 'options' | 'placeholder'> & {
    selected: Option['value'][];
}

const SelectedValue: FC<SelectedValueProps> = React.memo(({ options, selected, placeholder }) => {
    const filteredSelected = useMemo(() => selected.filter(el => el), [selected]);
    return filteredSelected.length > 0 ? (
        <Typography color='grayscale.shade100' variant='regularBody2'>
            {filteredSelected.map(s => options.find(o => o.value === s)?.title).join(', ')}
        </Typography>
    ) : (
        <Typography color='grayscale.shade85' variant='regularBody2'>
            {placeholder}
        </Typography>
    );
});

export const SelectField: FC<SelectFieldProps> = ({
    options,
    label,
    input: { value, onChange, name, ...inputProps },
    meta,
    mutate,
    placeholder,
    ...props
}) => {
    const renderValueComponent = useCallback(selected =>
        <SelectedValue options={options} selected={selected} placeholder={placeholder} />,
    [options, placeholder]);

    const filteredValue = useMemo(() =>
        Array.isArray(value) ? value : [value].filter(el => el),
    [value]);
    return (
        <FormControl fullWidth>
            <Typography variant='mediumBody2' mb={2}>
                {label}
            </Typography>
            <BaseSelect<Option['value'][]>
                data-testid={`select-${name}`}
                displayEmpty
                // @ts-ignore
                value={filteredValue}
                onChange={e => {
                    if (mutate)
                        mutate(e.target.value);
                    onChange(e.target.value);
                }}
                input={<Input />}
                IconComponent={ArrowDown24}
                MenuProps={{
                    PaperProps: {
                        sx: {
                            borderRadius: 3,
                            mt: 2,
                            boxShadow: boxShadows.shadow1,
                        },
                    },
                    MenuListProps: {
                        sx: {
                            py: 0,
                        },
                    },
                }}
                inputProps={{
                    name,
                    ...inputProps,
                }}
                renderValue={renderValueComponent}
                sx={{
                    [`& .${inputBaseClasses.input}`]: {
                        pt: 0,
                        pl: 0,
                        pb: 2,
                    },
                    [`&.${inputClasses.underline}::before, &.${inputClasses.underline}:hover::before`]: {
                        border: 0.5,
                        borderRadius: 1,
                        borderColor: 'grayscale.shade40',
                        height: 0,
                    },
                    [`&.${inputClasses.underline}::after`]: {
                        border: 0.5,
                        borderRadius: 1,
                        borderColor: 'grayscale.shade100',
                        height: 0,
                    },
                }}
                {...props}
            >
                {options.map(({ title, value }) => (
                    <MenuItem
                        key={value}
                        value={value}
                        sx={{
                            typography: 'mediumBody2',
                            color: 'grayscale.shade95',
                            py: 3,
                        }}
                    >
                        {title}
                    </MenuItem>
                ))}
            </BaseSelect>
            {meta.touched && meta.error && (
                <Typography variant='regularBody2' color='general.primary50'>
                    <forms.errorRenderer error={meta.error} />
                </Typography>
            )}
        </FormControl>
    );
};
